import {
  TerminalExecuteSsePayloadFromUnknownSchema,
  type TerminalExecutePayload,
  type TerminalFailureKind,
  type TerminalOutputItem,
} from '@terra/shared-dto-chat-types';
import { hasActiveChat } from '../../../store/chat-instance-presence';
import type { ExternalToolResultCoordinator } from '../external-tool-batch.types';
import { getExternalToolResultForRetry } from '../external-tool-result-coordinator';
import {
  scheduleTerminalExecution,
  type TerminalExecutionQueue,
} from '../terminal-execution-queue';
import {
  dropSuppressedForegroundExecute,
  handshakeFailureKind,
} from './handshake-gate';
import {
  buildCommandInput,
  runForegroundCommand,
  type TerminalExecuteRunCommand,
} from './portal-terminal-foreground-run';

type TerminalExecuteTracker = {
  registerExecution: (params: {
    toolCallId: string;
    chatId?: string;
  }) => 'execute' | 'duplicate';
};

function buildHandshakeFailureOutput({
  payload,
  failureKind,
}: {
  payload: TerminalExecutePayload;
  failureKind: Extract<TerminalFailureKind, 'timeout' | 'abort'>;
}): TerminalOutputItem {
  const output =
    failureKind === 'abort'
      ? 'Terminal execution aborted'
      : 'Timed out waiting for terminal batch';

  return {
    batchId: payload.batchId,
    toolCallId: payload.toolCallId,
    command: payload.displayCommand,
    exitCode: -1,
    output,
    outputLength: output.length,
    isTimeout: failureKind === 'timeout',
    failureKind,
  };
}

async function completeHandshakeFailure({
  coordinator,
  payload,
  failureKind,
}: {
  coordinator: ExternalToolResultCoordinator;
  payload: TerminalExecutePayload;
  failureKind: Extract<TerminalFailureKind, 'timeout' | 'abort'>;
}): Promise<void> {
  coordinator.registerRequest({
    batchId: payload.batchId,
    chatId: payload.chatId,
    toolCallId: payload.toolCallId,
    toolName: payload.toolName,
  });
  coordinator.clearExecutePending({
    batchId: payload.batchId,
    chatId: payload.chatId,
    toolCallId: payload.toolCallId,
  });

  await coordinator.completeRequest({
    batchId: payload.batchId,
    chatId: payload.chatId,
    toolCallId: payload.toolCallId,
    toolName: payload.toolName,
    status: 'error',
    result: buildHandshakeFailureOutput({ payload, failureKind }),
  });
}

async function reflushDuplicateResult({
  coordinator,
  payload,
}: {
  coordinator: ExternalToolResultCoordinator;
  payload: TerminalExecutePayload;
}): Promise<void> {
  coordinator.clearExecutePending({
    batchId: payload.batchId,
    chatId: payload.chatId,
    toolCallId: payload.toolCallId,
  });
  const cachedResult = getExternalToolResultForRetry({
    batchId: payload.batchId,
    chatId: payload.chatId,
    toolCallId: payload.toolCallId,
  });

  if (!cachedResult) {
    console.warn(
      '[PortalToolProvider] Duplicate toolCallId with no cached result to re-flush',
      {
        toolCallId: payload.toolCallId,
        toolName: payload.toolName,
        batchId: payload.batchId,
      },
    );

    return;
  }

  try {
    await coordinator.completeRequest(cachedResult);
  } catch (error: unknown) {
    console.warn(
      '[PortalToolProvider] Failed to re-flush duplicate tool result',
      {
        toolCallId: payload.toolCallId,
        toolName: payload.toolName,
        batchId: payload.batchId,
        error: error instanceof Error ? error : new Error(String(error)),
      },
    );
  }
}

export async function handlePortalTerminalExecute({
  terminalData,
  coordinator,
  tracker,
  queue,
  runCommand,
  isSessionSuppressed = () => false,
}: {
  terminalData: unknown;
  coordinator: ExternalToolResultCoordinator;
  tracker: TerminalExecuteTracker;
  queue: TerminalExecutionQueue;
  runCommand: TerminalExecuteRunCommand;
  isSessionSuppressed?: (sessionId: string) => boolean;
}): Promise<void> {
  const parsed =
    TerminalExecuteSsePayloadFromUnknownSchema.safeParse(terminalData);

  if (!parsed.success) {
    console.warn('[PortalToolProvider] Invalid terminal execute SSE payload', {
      error: parsed.error.message,
    });

    return;
  }

  const payload = parsed.data;

  if (!hasActiveChat(payload.chatId)) {
    console.info(
      '[PortalToolProvider] Skipping terminal execute; chat is not active in this window',
      { chatId: payload.chatId, toolCallId: payload.toolCallId },
    );

    return;
  }

  const dropIfForegroundStopped = () =>
    dropSuppressedForegroundExecute({
      coordinator,
      batchId: payload.batchId,
      toolCallId: payload.toolCallId,
      toolName: payload.toolName,
      abortResult: buildHandshakeFailureOutput({
        payload,
        failureKind: 'abort',
      }),
      chatId: payload.chatId,
      sessionId: payload.sessionId,
      isSessionSuppressed,
    });

  if (payload.isBackground) {
    const registration = tracker.registerExecution({
      toolCallId: payload.toolCallId,
      chatId: payload.chatId,
    });

    if (registration === 'duplicate') {
      return;
    }

    await scheduleTerminalExecution({
      sessionId: payload.sessionId,
      isBackground: true,
      execute: async () => {
        await runCommand({
          sessionId: payload.sessionId,
          batchId: payload.batchId,
          toolCallId: payload.toolCallId,
          input: buildCommandInput(payload),
        });
      },
      queue,
    });

    return;
  }

  if (dropIfForegroundStopped()) {
    return;
  }

  coordinator.markExecutePending({
    batchId: payload.batchId,
    chatId: payload.chatId,
    toolCallId: payload.toolCallId,
  });

  await coordinator.awaitPriorFlush(payload.chatId);

  if (dropIfForegroundStopped()) {
    return;
  }

  coordinator.markExecutePending({
    batchId: payload.batchId,
    chatId: payload.chatId,
    toolCallId: payload.toolCallId,
  });

  const declaration = await coordinator.awaitBatchDeclaration(payload.batchId);

  if (dropIfForegroundStopped()) {
    return;
  }

  const declarationFailure = handshakeFailureKind(declaration);

  if (declarationFailure) {
    const registration = tracker.registerExecution({
      toolCallId: payload.toolCallId,
      chatId: payload.chatId,
    });

    if (registration === 'duplicate') {
      await reflushDuplicateResult({ coordinator, payload });

      return;
    }

    await completeHandshakeFailure({
      coordinator,
      payload,
      failureKind: declarationFailure,
    });

    return;
  }

  const expected = await coordinator.awaitToolCallExpected({
    batchId: payload.batchId,
    toolCallId: payload.toolCallId,
  });

  if (dropIfForegroundStopped()) {
    return;
  }

  const expectedFailure = handshakeFailureKind(expected);

  if (expectedFailure) {
    const registration = tracker.registerExecution({
      toolCallId: payload.toolCallId,
      chatId: payload.chatId,
    });

    if (registration === 'duplicate') {
      await reflushDuplicateResult({ coordinator, payload });

      return;
    }

    await completeHandshakeFailure({
      coordinator,
      payload,
      failureKind: expectedFailure,
    });

    return;
  }

  const registration = tracker.registerExecution({
    toolCallId: payload.toolCallId,
    chatId: payload.chatId,
  });

  if (registration === 'duplicate') {
    await reflushDuplicateResult({ coordinator, payload });

    return;
  }

  coordinator.registerRequest({
    batchId: payload.batchId,
    chatId: payload.chatId,
    toolCallId: payload.toolCallId,
    toolName: payload.toolName,
  });

  await scheduleTerminalExecution({
    sessionId: payload.sessionId,
    isBackground: false,
    execute: async () => {
      if (dropIfForegroundStopped()) {
        return;
      }

      await runForegroundCommand({
        coordinator,
        payload,
        runCommand,
      });
    },
    queue,
  });
}
